/** Запись контекста: числовой идентификатор под именем `F` и участник, которому он принадлежит. */
export type UserSessionContext<F extends string> = { [K in F]: number } & { userId: number }

export interface UserSessionContextStore<F extends string> {
  /** Запомнить контекст. Недоступное хранилище исключения не бросает. */
  save: (context: UserSessionContext<F>) => void
  /** Контекст текущего участника, если он есть и принадлежит именно ему. */
  read: (userId: number | null | undefined) => UserSessionContext<F> | null
  /** Забыть контекст. */
  clear: () => void
}

/**
 * Хранилище контекста одного похода в `sessionStorage`, привязанное к участнику: запись
 * чужого участника игнорируется. Хранилище может быть недоступно (приватный режим Safari,
 * отключённые данные сайта, iframe с ограничениями) — тогда сохранение и очистка молча ничего не
 * делают, чтение даёт `null`: потерянный контекст не должен ронять страницу.
 *
 * @param storageKey ключ в `sessionStorage`
 * @param idField имя поля с числовым идентификатором в записи (`olympiadId`, `transactionId`)
 */
export function createUserSessionContext<F extends string>(
  storageKey: string,
  idField: F
): UserSessionContextStore<F> {
  return {
    save(context) {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(context))
      } catch {
        /* не сохранено — вызывающий переживает потерю контекста */
      }
    },

    read(userId) {
      if (typeof userId !== 'number') return null

      let raw: string | null = null
      try {
        raw = sessionStorage.getItem(storageKey)
      } catch {
        return null
      }
      if (!raw) return null

      try {
        const parsed: unknown = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') return null
        const record = parsed as Record<string, unknown>
        const id = record[idField]
        if (typeof id !== 'number' || !Number.isInteger(id) || id <= 0) return null
        if (record.userId !== userId) return null
        return { [idField]: id, userId } as UserSessionContext<F>
      } catch {
        return null
      }
    },

    clear() {
      try {
        sessionStorage.removeItem(storageKey)
      } catch {
        /* нечего чистить */
      }
    },
  }
}
